/**
 * Financial Privacy Mode — reusable React context.
 *
 * Display-only privacy gate for sensitive money figures (Gross / Refunds /
 * Net revenue, and by reuse Corporate Statements, Driver Earnings, future
 * reports). It NEVER changes how figures are calculated — it only controls
 * whether they're shown on screen.
 *
 * Operator MODE (server, operator-wide): "visible" | "hidden" | "protected".
 * Reveal CREDENTIAL (server, per-admin): PIN when set, else account password.
 * Reveal STATE lives here in browser memory with a 5-minute inactivity
 * auto-relock — no long-lived token is stored.
 */
import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import { api } from "./api";

const MASK = "••••••";
const RELOCK_MS = 5 * 60 * 1000; // 5 minutes of inactivity

const FinancialPrivacyContext = createContext(null);

export const useFinancialPrivacy = () => {
  const ctx = useContext(FinancialPrivacyContext);
  // Safe default so components used outside the provider don't crash —
  // they simply render values in the clear (no privacy gating).
  if (!ctx) {
    return {
      mode: "visible", hasPin: false, auditEnabled: false, masked: false, revealed: true,
      loading: false, lockedUntil: null,
      unlock: async () => {}, revealLocal: () => {}, hide: () => {},
      refresh: async () => {}, mask: (v) => v, logAudit: () => {},
    };
  }
  return ctx;
};

export const FinancialPrivacyProvider = ({ children }) => {
  // Privacy-first: start masked (protected) until the operator's real config
  // loads, so financial figures never flash on screen at login.
  const [config, setConfig] = useState({ mode: "protected", has_pin: false, audit_enabled: false, locked_until: null });
  const [loading, setLoading] = useState(true);
  const [revealed, setRevealed] = useState(false);
  const relockTimer = useRef(null);
  const auditRef = useRef(false);

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/admin/privacy/config");
      setConfig(data);
      auditRef.current = !!data.audit_enabled;
    } catch {
      // On failure stay privacy-first (masked) rather than exposing figures.
      setConfig({ mode: "protected", has_pin: false, audit_enabled: false, locked_until: null });
      auditRef.current = false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Append-only Financial Access Audit — fire-and-forget, only when enabled.
  const logAudit = useCallback((action, meta) => {
    if (!auditRef.current) return;
    api.post("/admin/privacy/audit", { action, meta: meta || {} }).catch(() => {});
  }, []);

  const clearRelock = () => {
    if (relockTimer.current) { clearTimeout(relockTimer.current); relockTimer.current = null; }
  };
  const armRelock = useCallback(() => {
    clearRelock();
    relockTimer.current = setTimeout(() => {
      setRevealed(false);
      logAudit("auto_relock", { after_minutes: 5 });
    }, RELOCK_MS);
  }, [logAudit]);

  // While revealed, reset the inactivity countdown on any user activity.
  useEffect(() => {
    if (!revealed) { clearRelock(); return; }
    armRelock();
    const bump = () => armRelock();
    const evts = ["mousemove", "keydown", "click", "scroll", "touchstart"];
    evts.forEach((e) => window.addEventListener(e, bump, { passive: true }));
    return () => {
      evts.forEach((e) => window.removeEventListener(e, bump));
      clearRelock();
    };
  }, [revealed, armRelock]);

  // If the operator switches mode to "visible", drop any reveal state.
  useEffect(() => {
    if (config.mode === "visible") setRevealed(false);
  }, [config.mode]);

  const unlock = useCallback(async (credential) => {
    await api.post("/admin/privacy/unlock", { credential });
    setRevealed(true);
    logAudit("revealed", { method: "credential" });
    return true;
  }, [logAudit]);

  const revealLocal = useCallback(() => {
    setRevealed(true);
    logAudit("revealed", { method: "none" });
  }, [logAudit]);

  const hide = useCallback(() => {
    setRevealed(false);
    logAudit("hidden", { manual: true });
  }, [logAudit]);

  const masked = config.mode !== "visible" && !revealed;
  const mask = useCallback((value) => (masked ? MASK : value), [masked]);

  const value = {
    mode: config.mode,
    hasPin: !!config.has_pin,
    auditEnabled: !!config.audit_enabled,
    lockedUntil: config.locked_until || null,
    loading,
    revealed,
    masked,
    mask,
    unlock,
    revealLocal,
    hide,
    refresh,
    logAudit,
  };

  return (
    <FinancialPrivacyContext.Provider value={value}>
      {children}
    </FinancialPrivacyContext.Provider>
  );
};

export { MASK };
